/// <reference types="vite/client" />

import {
    createRootRouteWithContext,
    stripSearchParams,
} from '@tanstack/react-router';

import toastStylesHref from 'react-toastify/ReactToastify.css?url';
import appCss from '~/tailwind.css?url';
import { ThemeProvider } from '~/components/Providers/ThemeProvider';
import { ReactNode } from 'react';
import { z } from 'zod';
import {
    BackgroundProvider,
    GlobalBackground,
} from '~/components/Providers/BackgroundProvider';
import { TooltipProvider } from '~/primitives/tooltip';
import { AuthProvider } from '~/components/Providers/AuthProvider';
import { QueryClient } from '@tanstack/query-core';
import { PublicAuthData } from '~/auth/session';
import { APIProvider } from '~/components/Providers/APIProvider';
import { ToastError, toastKeys } from '~/util/types/toasts';
import { userNotificationsQuery } from '~/util/queries/public/users';
import { getAuth } from '~/auth/getAuth';
import { logoutServer } from '~/auth/logout';
import { RootDocument } from './-RootDocument';
import { RootComponent } from './-RootComponent';
import {
    getPrimaryColorCookie,
    getThemeVarsForSSR,
} from '~/components/Providers/theme';
import { setApiRequestContext } from '~/components/Providers/APIClient';

export interface RouterContext {
    queryClient: QueryClient;
    auth: Partial<PublicAuthData> | null;
}

const rootParamsSchema = z.object({
    list: z.enum(['classic', 'platformer']).default('classic').catch('classic'),
    showToast: z.enum(toastKeys).optional().catch(undefined),
});

const defaultRootParams = rootParamsSchema.parse({});

export const Route = createRootRouteWithContext<RouterContext>()({
    validateSearch: rootParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultRootParams)],
    },
    beforeLoad: async ({ context: { auth } }) => {
        if (auth) {
            setApiRequestContext({
                token: auth.access_token ?? undefined,
                hasStaffScope: (auth.scopes?.length ?? 0) > 0,
            });
            return { auth, authed: !!auth?.access_token };
        }

        try {
            auth = await getAuth();
        } catch (error: unknown) {
            console.warn('Failed to get auth data: ', error);
            if (error instanceof ToastError) {
                await logoutServer({
                    data: {
                        showToast: error.toastKey,
                    },
                });
            }
        }

        setApiRequestContext({
            token: auth?.access_token ?? undefined,
            hasStaffScope: (auth?.scopes?.length ?? 0) > 0,
        });

        return {
            auth,
            authed: !!auth?.access_token,
        };
    },
    loader: async ({ context: { queryClient, auth, authed } }) => {
        const primaryColor = await getPrimaryColorCookie();

        return {
            auth,
            primaryColor,
            cssVariables: getThemeVarsForSSR(primaryColor),
            notifications: authed
                ? await queryClient
                      .fetchQuery(userNotificationsQuery())
                      .catch(() => [])
                : [],
        };
    },
    headers: () => ({
        'Cache-Control': 'private, no-store, max-age=0',
    }),
    head: () => ({
        meta: [
            { title: 'All Rated Extreme Demons List' },
            {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1',
            },
            { charSet: 'utf-8' },
            {
                name: 'robots',
                content: import.meta.env.VITE_DEV_PREVIEW
                    ? 'noindex'
                    : 'index, follow',
            },
        ],
        links: [
            { rel: 'stylesheet', href: toastStylesHref },
            { rel: 'stylesheet', href: appCss },
        ],
    }),
    staleTime: 900_000,
    shellComponent: RootLayoutComponent,
});

function RootLayoutComponent({ children }: Readonly<{ children: ReactNode }>) {
    const { auth, primaryColor } = Route.useLoaderData({
        select: ({ auth, primaryColor }) => ({ auth, primaryColor }),
    });

    return (
        <RootDocument>
            <AuthProvider initial={auth}>
                <APIProvider>
                    <ThemeProvider initialColor={primaryColor}>
                        <BackgroundProvider>
                            <TooltipProvider delayDuration={150}>
                                <GlobalBackground>
                                    <RootComponent>{children}</RootComponent>
                                </GlobalBackground>
                            </TooltipProvider>
                        </BackgroundProvider>
                    </ThemeProvider>
                </APIProvider>
            </AuthProvider>
        </RootDocument>
    );
}
